/**
 * Scan Agent - Scanning Session
 * 
 * Page scanning interface for agents. Wraps UnifiedScanner with
 * request/book context and handles the full scanning workflow.
 * 
 * Supports two modes:
 * - Request-based: /scan-agent/scan/request/:requestId (parent requested book)
 * - Direct book: /scan-agent/scan/book/:bookId (proactive scanning)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  BookOpen,
} from 'lucide-react';
import { glassStyles } from '@/lib/design';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase/client';
import { UnifiedScanner, type BookMetadata } from '@/components/scanner/UnifiedScanner';
import { ScannerErrorBoundary } from '@/components/scanner/ScannerErrorBoundary';
import {
  updateScanRequestStatus,
  createScannedBook,
  type BookScanRequest,
  type ScannedBook,
} from '@/services/scan';
import {
  saveCapturesToStorage,
  triggerBackgroundProcessing,
  getScannedBook,
} from '@/services/scannedBookService';

// ============================================================================
// Types
// ============================================================================

interface CapturedPage {
  pageNumber: number;
  imageBase64: string;
}

interface BookInfo {
  title: string;
  author: string | null;
  coverImageUrl: string | null;
  isbn: string | null;
  publisher: string | null;
}

// ============================================================================
// Component
// ============================================================================

export function ScanAgentScanSession() {
  // Support both request-based and direct book scanning
  const { requestId, bookId: directBookId } = useParams<{ requestId?: string; bookId?: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [request, setRequest] = useState<BookScanRequest | null>(null);
  const [existingBook, setExistingBook] = useState<ScannedBook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Scanning state
  const [showScanner, setShowScanner] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [scannedPages, setScannedPages] = useState<number[]>([]);
  const [captures, setCaptures] = useState<CapturedPage[]>([]);
  const [bookId, setBookId] = useState<string | null>(directBookId || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);

  // Derived book info (from request or existing book) - memoized to prevent unnecessary re-renders
  const bookInfo: BookInfo | null = useMemo(() => {
    if (request) {
      return {
        title: request.title,
        author: request.author,
        coverImageUrl: request.cover_image_url,
        isbn: request.isbn,
        publisher: request.publisher,
      };
    }
    if (existingBook) {
      return {
        title: existingBook.title,
        author: existingBook.author,
        coverImageUrl: existingBook.cover_image_url,
        isbn: existingBook.isbn,
        publisher: existingBook.publisher,
      };
    }
    return null;
  }, [request, existingBook]);

  // ============================================================================
  // Load Request OR Book
  // ============================================================================

  useEffect(() => {
    async function loadContext() {
      // Mode 1: Direct book scanning (proactive)
      if (directBookId) {
        try {
          const book = await getScannedBook(directBookId);
          if (!book) {
            setError('Book not found');
            return;
          }
          setExistingBook(book);
          setBookId(directBookId);
        } catch (err) {
          console.error('Error loading book:', err);
          setError('Failed to load book');
        } finally {
          setLoading(false);
        }
        return;
      }

      // Mode 2: Request-based scanning
      if (!requestId) {
        setError('No request or book ID provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('book_scan_requests')
          .select('id, isbn, title, author, cover_image_url, publisher, requested_by, status, assigned_to, assigned_at, resolved_book_id, resolved_at, priority, notes, created_at, updated_at')
          .eq('id', requestId)
          .maybeSingle();

        if (fetchError || !data) {
          setError('Request not found');
          return;
        }

        setRequest(data as BookScanRequest);

        // If request already has a resolved book, use it
        if (data.resolved_book_id) {
          setBookId(data.resolved_book_id);
        }

        // Update status to scanning if not already
        if (data.status === 'assigned') {
          await updateScanRequestStatus(requestId, 'scanning');
        }
      } catch (err) {
        console.error('Error loading request:', err);
        setError('Failed to load request');
      } finally {
        setLoading(false);
      }
    }

    loadContext();
  }, [requestId, directBookId]);

  // ============================================================================
  // Scanning Handlers
  // ============================================================================

  const handleStartScanning = useCallback(async () => {
    if (!profile?.id) return;

    // For direct book mode, book already exists
    if (directBookId) {
      setShowScanner(true);
      return;
    }

    // For request mode, create book if needed
    if (!request) return;

    try {
      // Create the book if it doesn't exist yet
      if (!bookId) {
        const book = await createScannedBook(
          profile.id,
          request.title,
          {
            isbn: request.isbn || undefined,
            author: request.author || undefined,
            coverImageUrl: request.cover_image_url || undefined,
            publisher: request.publisher || undefined,
          },
          request.id
        );
        setBookId(book.id);
      }

      setShowScanner(true);
    } catch (err) {
      console.error('Error starting scan:', err);
      setError('Failed to start scanning');
    }
  }, [request, profile?.id, bookId, directBookId]);

  const handleIsbnDetected = useCallback((isbn: string, metadata?: BookMetadata | null) => {
    // ISBN already known from request, just continue
    console.log('[ScanSession] ISBN detected:', isbn);
  }, []);

  const handleIsbnSkip = useCallback(() => {
    // Already have book info from request
  }, []);

  const handleCapture = useCallback(async (imageBase64: string, pageNumber: number) => {
    setCaptures(prev => [...prev, { pageNumber, imageBase64 }]);
    setScannedPages(prev => [...prev, pageNumber]);
    setCurrentPage(pageNumber + 1);
  }, []);

  const handleScanComplete = useCallback(async () => {
    if (!bookId || captures.length === 0) {
      setShowScanner(false);
      return;
    }

    setIsProcessing(true);
    setShowScanner(false);

    try {
      // Save captures to storage - MUST complete before showing success
      await saveCapturesToStorage(
        bookId,
        captures.map(c => ({
          pageNumber: c.pageNumber,
          rawImage: c.imageBase64,
        }))
      );

      // Update request status to editing (if this is a request-based scan)
      if (requestId) {
        await supabase
          .from('book_scan_requests')
          .update({
            status: 'editing',
            resolved_book_id: bookId,
          })
          .eq('id', requestId);
      }

      // FIRE-AND-FORGET: Trigger background OCR processing
      // Processing runs on Supabase Edge Functions - survives browser close
      // User can navigate away immediately; book will appear in "My Books" when ready
      triggerBackgroundProcessing(bookId)
        .then(result => {
          if (!result.success) {
            console.error('[ScanComplete] Background processing failed:', result.error);
          } else {
            console.log('[ScanComplete] Background processing complete');
          }
        })
        .catch(err => {
          console.error('[ScanComplete] Background processing error:', err);
        });

      // Show success immediately - user can navigate away
      setScanComplete(true);
    } catch (err) {
      console.error('Error completing scan:', err);
      setError('Failed to save scanned pages');
    } finally {
      setIsProcessing(false);
    }
  }, [bookId, captures, requestId]);

  const handleScannerClose = useCallback(() => {
    setShowScanner(false);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#5C2D91] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto py-12">
        <div className="p-6 rounded-2xl bg-[#FF453A]/10 border border-[#FF453A]/20 text-center">
          <AlertCircle className="w-12 h-12 text-[#FF453A] mx-auto mb-4" />
          <div className="text-[#1C1C1E] font-medium mb-2">{error}</div>
          <button
            onClick={() => navigate('/scan-agent')}
            className="text-[#5C2D91] text-sm"
          >
            Back to Queue
          </button>
        </div>
      </div>
    );
  }

  if (!bookInfo) {
    return null;
  }

  // Show scanner
  if (showScanner) {
    return (
      <ScannerErrorBoundary onClose={handleScannerClose}>
        <UnifiedScanner
          onIsbnDetected={handleIsbnDetected}
          onIsbnSkip={handleIsbnSkip}
          onCapture={handleCapture}
          onClose={handleScannerClose}
          onComplete={handleScanComplete}
          currentPage={currentPage}
          scannedPages={scannedPages}
          isProcessing={isProcessing}
          bookInfo={{
            title: bookInfo.title,
            author: bookInfo.author,
            coverImageUrl: bookInfo.coverImageUrl,
          }}
          skipIsbn={true} // Already have book info
        />
      </ScannerErrorBoundary>
    );
  }

  // Show processing state
  if (isProcessing) {
    return (
      <div className="max-w-lg mx-auto py-12 text-center">
        <Loader2 className="w-12 h-12 text-[#5C2D91] animate-spin mx-auto mb-4" />
        <div className="text-[#1C1C1E] font-medium mb-2">Processing scanned pages...</div>
        <div className="text-sm text-[#8E8E93]">
          {captures.length} page{captures.length !== 1 ? 's' : ''} being uploaded
        </div>
      </div>
    );
  }

  // Show completion state with device-aware guidance
  if (scanComplete) {
    // Detect if on mobile/tablet - suggest switching to desktop for editing
    const isMobileDevice = typeof window !== 'undefined' && 
      (/ipad|iphone|ipod|android/i.test(navigator.userAgent) || 
       ('ontouchend' in document && window.innerWidth < 1024));
    
    return (
      <div className="max-w-lg mx-auto py-12 text-center">
        <div className="w-16 h-16 bg-[#30D158]/15 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-[#30D158]" />
        </div>
        <h2 className="text-xl font-semibold text-[#1C1C1E] mb-2">Scanning Complete!</h2>
        <p className="text-[#8E8E93] mb-4">
          {captures.length} page{captures.length !== 1 ? 's' : ''} uploaded successfully.
          Text extraction is processing in the background — you can navigate away safely.
        </p>
        
        {/* Device-aware guidance */}
        {isMobileDevice && (
          <div className="p-4 rounded-xl bg-[#FF9F0A]/10 border border-[#FF9F0A]/20 mb-6">
            <p className="text-sm text-[#FF9F0A]">
              💡 <strong>Next step:</strong> Open this book on your computer for easier content editing. 
              The book will appear in your "My Active Work" section.
            </p>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => navigate('/scan-agent')}
            className="w-full sm:w-auto px-4 py-2 rounded-xl bg-black/[0.03] text-[#636366] hover:bg-black/[0.05] transition-all"
          >
            Back to Queue
          </button>
          {bookId && (
            <button
              onClick={() => navigate(`/scan-agent/edit/${bookId}`)}
              className={`w-full sm:w-auto px-4 py-2 rounded-xl font-medium transition-all hover:brightness-110 ${
                isMobileDevice 
                  ? 'bg-black/[0.05] text-[#636366]' 
                  : 'bg-[#30D158] text-white'
              }`}
            >
              {isMobileDevice ? 'Edit on This Device' : 'Edit Content'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Determine mode label
  const isProactiveMode = !!directBookId;
  const modeLabel = isProactiveMode ? 'New Book' : 'Parent Request';

  // Show pre-scan info
  return (
    <div className="max-w-2xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/scan-agent')}
        className="flex items-center gap-2 text-[#8E8E93] hover:text-[#1C1C1E] mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Queue
      </button>

      {/* Mode Badge */}
      <div className="mb-4">
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
          isProactiveMode 
            ? 'bg-[#30D158]/15 text-[#30D158]' 
            : 'bg-[#5C2D91]/15 text-[#5C2D91]'
        }`}>
          {modeLabel}
        </span>
      </div>

      {/* Book Info Card */}
      <div className="p-6 rounded-2xl mb-6" style={glassStyles.card}>
        <div className="flex items-start gap-4">
          {/* Cover */}
          <div className="w-24 h-32 bg-black/[0.03] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {bookInfo.coverImageUrl ? (
              <img 
                src={bookInfo.coverImageUrl} 
                alt={bookInfo.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <BookOpen className="w-8 h-8 text-[#AEAEB2]" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-[#1C1C1E] mb-1">{bookInfo.title}</h2>
            {bookInfo.author && (
              <p className="text-[#8E8E93] mb-2">{bookInfo.author}</p>
            )}
            <div className="space-y-1 text-sm text-[#8E8E93]">
              {bookInfo.isbn && <div>ISBN: {bookInfo.isbn}</div>}
              {bookInfo.publisher && <div>Publisher: {bookInfo.publisher}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 rounded-2xl mb-6" style={glassStyles.cardSubtle}>
        <h3 className="font-medium text-[#1C1C1E] mb-3">Scanning Instructions</h3>
        <ul className="space-y-2 text-sm text-[#636366]">
          <li className="flex items-start gap-2">
            <span className="text-[#5C2D91]">1.</span>
            Place the book on a flat, well-lit surface
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#5C2D91]">2.</span>
            For board books, scan the full two-page spread together
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#5C2D91]">3.</span>
            Hold the camera steady until auto-capture triggers
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#5C2D91]">4.</span>
            Tap "Done" when all pages are scanned
          </li>
        </ul>
      </div>

      {/* Start Button */}
      <button
        onClick={handleStartScanning}
        className="w-full py-4 rounded-2xl bg-[#5C2D91] text-white font-medium text-lg transition-all hover:brightness-110"
      >
        Start Scanning
      </button>
    </div>
  );
}

export default ScanAgentScanSession;
